import { useState } from "react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { SpacePicker } from "@/components/booking/SpacePicker";
import { ToggleChips } from "@/components/admin/ToggleChips";
import { ClassBookingResult } from "@/components/admin/ClassBookingResult";
import { DAYS, SLOTS } from "@/lib/studio";
import { api, apiError } from "@/lib/api";

const DAY_OPTIONS = DAYS.map((d, i) => ({ id: i, label: d.slice(0, 3) }));
const SLOT_OPTIONS = SLOTS.map((s) => ({ id: s.id, label: s.time }));
const EMPTY = { class_name: "", start_date: "", end_date: "", weekdays: [0, 1, 2, 3, 4], slots: [], space: null, notes: "" };

export const ClassBookingForm = ({ onDone }) => {
  const [form, setForm] = useState(EMPTY);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/admin/bulk-bookings", form);
      setResult(data);
      toast.success(`${data.created} session(s) booked for ${form.class_name}`);
      onDone?.();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="grid gap-8 lg:grid-cols-12">
      <form onSubmit={submit} className="surface space-y-6 p-6 md:p-8 lg:col-span-7" data-testid="class-booking-form">
        <h2 className="font-display text-lg font-semibold uppercase text-white">Book a class series</h2>
        <Input required placeholder="Class name (e.g. DMC Year 2 — Podcasting)" maxLength={80} value={form.class_name} onChange={(e) => set("class_name")(e.target.value)} data-testid="class-name-input" />
        <div className="grid gap-4 sm:grid-cols-2">
          <label className="space-y-2 text-xs text-zinc-400">From
            <Input type="date" required value={form.start_date} onChange={(e) => set("start_date")(e.target.value)} className="[color-scheme:dark]" data-testid="class-start-date" />
          </label>
          <label className="space-y-2 text-xs text-zinc-400">To
            <Input type="date" required value={form.end_date} onChange={(e) => set("end_date")(e.target.value)} className="[color-scheme:dark]" data-testid="class-end-date" />
          </label>
        </div>
        <div className="space-y-2"><p className="text-xs text-zinc-400">Weekdays</p><ToggleChips options={DAY_OPTIONS} values={form.weekdays} onChange={set("weekdays")} testPrefix="class-day" /></div>
        <div className="space-y-2"><p className="text-xs text-zinc-400">Sessions</p><ToggleChips options={SLOT_OPTIONS} values={form.slots} onChange={set("slots")} testPrefix="class-slot" /></div>
        <div className="space-y-2"><p className="text-xs text-zinc-400">Space (optional)</p><SpacePicker value={form.space} onChange={set("space")} /></div>
        <Textarea placeholder="Notes (optional)" maxLength={500} value={form.notes} onChange={(e) => set("notes")(e.target.value)} data-testid="class-notes-input" />
        <button type="submit" disabled={loading || !form.slots.length || !form.weekdays.length} className="btn-riot w-full" data-testid="class-submit-btn">
          {loading ? "Booking…" : "Book all matching sessions"}
        </button>
      </form>
      <div className="lg:col-span-5"><ClassBookingResult result={result} /></div>
    </div>
  );
};
